import { useInfiniteQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { TransferRecordResponse } from 'types/transferRecord'

interface TransferRecordsListResponse {
  success: boolean
  result: TransferRecordResponse[]
  count: number
}

interface UseTransferRecordsListParams {
  residentId: string
  limit?: number
}

interface UseTransferRecordsListReturn {
  records: TransferRecordResponse[]
  isLoading: boolean
  refetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export const useTransferRecordsList = ({
  residentId,
  limit = 100
}: UseTransferRecordsListParams): UseTransferRecordsListReturn => {
  const fetchTransferRecords = async ({ pageParam = 0 }) => {
    const url = `/api/residents/${residentId}/transfer-records?limit=${limit}&offset=${pageParam}`
    const response = (await getJSON({ url })) as TransferRecordsListResponse

    return {
      records: response.result || [],
      totalCount: response.count || 0,
      nextOffset:
        response.result?.length === limit ? pageParam + limit : undefined
    }
  }

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(
    ['transfer-records', residentId],
    fetchTransferRecords,
    {
      getNextPageParam: (lastPage) => lastPage.nextOffset,
      enabled: !!residentId
    }
  )

  const records = data?.pages.flatMap((page) => page.records) || []

  return {
    records,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage
  }
}
